<template>
  <modal :modal-is-open="modalIsOpen" :modal-title="$t('purchaseLicense.comparePlans')" @on-close="$emit('on-close')">
    <div class="content">
      <table>
        <thead>
          <tr>
            <th v-for="(columnTitle, index) in columnTitles" :key="`columnTitle_${index}`">
              {{ columnTitle }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(feature, index) in features" :key="`feature_${index}`">
            <td>
              {{ feature.title }}
            </td>
            <td v-for="(isEnabledInPlan, index) in feature.isEnabledInPlans" :key="`isEnabled_${index}`">
              <label v-if="isEnabledInPlan" class="checkmarkContainer">
                <input :checked="isEnabledInPlan" type="checkbox" />
                <span class="checkmark"></span>
              </label>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </modal>
</template>

<style lang="scss" scoped>
@import "@/variables.scss";

table {
  border-spacing: 0;
  text-align: center;

  th {
    width: 120px;
    border-top: 1rem solid transparent;
    border-bottom: 1.5rem solid transparent;
    font-size: 20px;

    &:first-of-type {
      text-align: left;
      width: 240px;
      padding-left: 20px;
    }
  }

  td {
    border-top: 1rem solid transparent;
    border-bottom: 1rem solid transparent;

    &:first-of-type {
      text-align: left;
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
      padding-left: 20px;
    }

    &:last-of-type {
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }
  }

  tr:nth-of-type(2n) {
    background-color: #f3f6f7;
  }
}

.checkmarkContainer {
  position: relative;
  top: -4px;
  padding-left: 1.5rem;
  transition: all $site-transition-speed;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.checkmarkContainer input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 1px;
  left: 0;
  height: 1.5rem;
  width: 1.5rem;
  transition: all $site-transition-speed;
  border-radius: 2rem;
  border: 1px solid #dddddd;
  box-shadow: 0px 0px 14px $color-box-shadow;
  background: white;
}

.checkmarkContainer input:checked~.checkmark {
  background-color: $color-primary;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkmarkContainer input:checked~.checkmark:after {
  display: block;
}

.checkmarkContainer .checkmark:after {
  left: 9px;
  top: 4px;
  width: 3px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
</style>

<script>
import Modal from "./Modal";

const enabledInTrialPlan = true;
const enabledInPremiumPlan = true;
const disabledInPremiumPlan = false;
const enabledInProPlan = true;

export default {
  name: "ComparePlansModal",

  components: {
    Modal
  },

  props: {
    modalIsOpen: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      columnTitles: this.$t("comparePlansModal.columnTitles"),
      features: [
        {
          title: this.$t(
            "comparePlansModal.feature.visualiseBalanceSheetAndProfitLoss"
          ),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t(
            "comparePlansModal.feature.multipleHistoricFinancialStatements"
          ),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.manualCompany"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.visualiseCashFlow"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.manualFinancialStatements"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.compareCompanies"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.changeAmounts"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan,
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.saveChanges"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.keyRatios"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.trends"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            enabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.simulation"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            disabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.industryStatistics"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            disabledInPremiumPlan,
            enabledInProPlan
          ]
        },
        {
          title: this.$t("comparePlansModal.feature.analysisGuide"),
          isEnabledInPlans: [
            enabledInTrialPlan,
            disabledInPremiumPlan,
            enabledInProPlan
          ]
        }
      ]
    };
  }
};
</script>
